import renderMarkdown from '../lib/markdown_render'

// <legal-chat> — the template-editor legal assistant (Sprint T8).
//
// Owns ONE conversation state (history of { role, content } with content as
// RAW markdown) and ONE chat DOM ([data-chat-part="body"]). The body node
// physically travels between the side panel slot and the expand modal slot,
// so the panel chat and the modal chat are literally the same conversation:
// same nodes, same listeners, same input value, one source of truth.
//
// Assistant replies render as sanitized rich text (lib/markdown_render);
// per-message COPY puts the raw markdown on the clipboard.
export default class extends HTMLElement {
  connectedCallback () {
    this.history = []
    this.attachments = []
    this.currentModel = null

    this.panelSlot = this.querySelector('[data-chat-slot="panel"]')
    this.modalSlot = this.querySelector('[data-chat-slot="modal"]')
    this.body = this.querySelector('[data-chat-part="body"]')
    this.overlay = this.querySelector('[data-chat-part="overlay"]')
    this.modal = this.querySelector('[data-chat-part="modal"]')
    this.messages = this.querySelector('[data-chat-part="messages"]')
    this.form = this.querySelector('[data-chat-part="form"]')
    this.input = this.querySelector('[data-chat-part="input"]')
    this.sendButton = this.querySelector('[data-chat-part="send"]')
    this.chips = this.querySelector('[data-chat-part="chips"]')
    this.attachButton = this.querySelector('[data-chat-part="attach"]')
    this.fileInput = this.querySelector('[data-chat-part="file"]')
    this.modelSelect = this.querySelector('[data-chat-part="model-select"]')
    this.modelPrivacy = this.querySelector('[data-chat-part="model-privacy"]')
    this.actionsTemplate = this.querySelector('template[data-chat-part="message-actions"]')
    this.chipTemplate = this.querySelector('template[data-chat-part="attachment-chip"]')

    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ''

    this.onKeydown = (event) => {
      if (event.key === 'Escape' && !this.overlay.hidden) this.closeModal()
    }

    document.addEventListener('keydown', this.onKeydown)

    this.form.addEventListener('submit', (event) => {
      event.preventDefault()
      this.sendMessage()
    })

    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        this.sendMessage()
      }
    })

    this.querySelector('[data-chat-part="panel-expand"]')?.addEventListener('click', () => this.openModal())
    this.querySelector('[data-chat-part="modal-close"]')?.addEventListener('click', () => this.closeModal())

    this.querySelector('[data-chat-part="modal-copy"]')?.addEventListener('click', (event) => {
      this.copyToClipboard(this.conversationMarkdown(), event.currentTarget)
    })

    this.querySelector('[data-chat-part="modal-maximize"]')?.addEventListener('click', () => {
      this.modal.classList.remove('minimized')
      this.modal.classList.toggle('maximized')
      this.scrollToBottom()
    })

    this.querySelector('[data-chat-part="modal-minimize"]')?.addEventListener('click', (event) => {
      event.stopPropagation()
      this.modal.classList.remove('maximized')
      this.modal.classList.toggle('minimized')
    })

    // A minimized modal is a title pill: clicking it restores the window.
    this.querySelector('[data-chat-part="modal-header"]')?.addEventListener('click', () => {
      if (this.modal.classList.contains('minimized')) this.modal.classList.remove('minimized')
    })

    if (this.attachButton && this.fileInput) {
      this.attachButton.addEventListener('click', () => {
        if (this.attachments.length >= this.maxAttachments()) {
          this.flashNotice(this.dataset.tTooManyAttachments)

          return
        }

        this.fileInput.click()
      })

      this.fileInput.addEventListener('change', () => {
        const file = this.fileInput.files[0]

        this.fileInput.value = ''

        if (file) this.uploadAttachment(file)
      })
    }

    // <docs-assistant> (data-lazy-models) defers the models fetch to first
    // open so the floating help widget adds no request to ordinary page
    // loads. The legal-chat panel keeps loading models on connect.
    if (this.dataset.lazyModels !== 'true') this.loadModels()
  }

  disconnectedCallback () {
    document.removeEventListener('keydown', this.onKeydown)
  }

  maxAttachments () {
    return parseInt(this.dataset.maxAttachments || '3', 10)
  }

  // ---- Modal travel: the chat body MOVES between slots ----
  openModal () {
    this.modalSlot.appendChild(this.body)
    this.overlay.hidden = false
    this.modal.classList.remove('minimized', 'maximized')
    this.scrollToBottom()
    this.input.focus()
  }

  closeModal () {
    this.panelSlot.appendChild(this.body)
    this.overlay.hidden = true
    this.modal.classList.remove('minimized', 'maximized')
    this.scrollToBottom()
  }

  scrollToBottom () {
    this.messages.scrollTop = this.messages.scrollHeight
  }

  // ---- Models ----
  // Fetches the selectable model catalog and populates the dropdown.
  // Resolves true on success, false on any failure (HTTP error or network),
  // WITHOUT ever leaving the <select> permanently disabled — a stuck,
  // un-selectable dropdown was the docs-assistant "model selector frozen
  // until full page refresh" bug (the cold Bedrock catalog build can take
  // ~18s, and any abandoned/errored first fetch used to leave the control
  // disabled with no retry). The change listener is bound exactly once so
  // retries (re-open) don't stack duplicate POSTs.
  loadModels () {
    if (!this.modelSelect) return Promise.resolve(false)

    this.bindModelChangeOnce()

    // Visible "loading" affordance instead of a silent disabled box.
    this.modelSelect.replaceChildren(new Option(this.dataset.tLoadingModels || 'Loading models…', ''))
    this.modelSelect.disabled = true

    return fetch(this.dataset.modelsUrl, { headers: { Accept: 'application/json' } }).then((response) => {
      return response.json().then((data) => ({ ok: response.ok, data }))
    }).then((result) => {
      if (!result.ok) {
        // Keep the control usable so the user can retry (re-open) instead of
        // being stuck; surface the server-provided reason.
        this.modelSelect.replaceChildren(new Option(result.data.error || (this.dataset.tModelsUnavailable || 'Unavailable — reopen to retry'), ''))
        this.modelSelect.disabled = false

        return false
      }

      this.currentModel = result.data.selected || result.data.default
      this.modelSelect.replaceChildren()

      const tooltip = (model) => [
        model.quality ? `Quality: ${model.quality}` : null,
        model.speed ? `Speed: ${model.speed}` : null,
        model.cost ? `Relative cost: ${model.cost}` : null,
        model.context ? `Context window: ${model.context}` : null,
        model.note || null
      ].filter(Boolean).join('\n')

      const models = result.data.models || []

      models.forEach((model) => {
        const option = new Option(model.name, model.id)

        option.title = tooltip(model)
        option.selected = model.id === this.currentModel

        this.modelSelect.appendChild(option)
      })

      this.modelSelect.disabled = false

      if (this.modelPrivacy) this.modelPrivacy.textContent = result.data.privacy || ''

      return true
    }).catch(() => {
      this.modelSelect.replaceChildren(new Option(this.dataset.tModelsUnavailable || 'Unavailable — reopen to retry', ''))
      this.modelSelect.disabled = false

      return false
    })
  }

  // Bind the model-change POST handler a single time, regardless of how many
  // times loadModels() runs (first load + any retries).
  bindModelChangeOnce () {
    if (this.modelChangeBound) return
    this.modelChangeBound = true

    this.modelSelect.addEventListener('change', () => {
      const chosen = this.modelSelect.value

      if (!chosen || chosen === this.currentModel) return

      this.modelSelect.disabled = true

      fetch(this.dataset.modelsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': this.csrfToken },
        body: JSON.stringify({ model_id: chosen })
      }).then((response) => {
        this.modelSelect.disabled = false

        if (response.ok) {
          this.currentModel = chosen
        } else {
          this.modelSelect.value = this.currentModel
        }
      }).catch(() => {
        this.modelSelect.disabled = false
        this.modelSelect.value = this.currentModel
      })
    })
  }

  // ---- Messages ----
  buildElement (tag, className, text) {
    const node = document.createElement(tag)

    if (className) node.className = className
    if (text) node.textContent = text

    return node
  }

  appendBubble (role, markdown) {
    const wrap = this.buildElement('div', role === 'user' ? 'chat chat-end' : 'chat chat-start')
    const bubble = this.buildElement('div', `chat-bubble text-sm${role === 'user' ? ' chat-bubble-primary whitespace-pre-wrap' : ' thor-chat-md'}`)

    if (role === 'user') {
      bubble.textContent = markdown
    } else if (markdown) {
      this.renderAssistant(wrap, bubble, markdown)
    } else {
      bubble.textContent = '…'
    }

    wrap.appendChild(bubble)
    this.messages.appendChild(wrap)
    this.scrollToBottom()

    return { wrap, bubble }
  }

  renderAssistant (wrap, bubble, markdown) {
    bubble.classList.add('thor-chat-md')
    bubble.classList.remove('whitespace-pre-wrap')
    bubble.replaceChildren(renderMarkdown(markdown))

    if (!this.actionsTemplate) return

    wrap.querySelector('[data-chat-actions]')?.remove()

    const actions = this.actionsTemplate.content.cloneNode(true).firstElementChild

    actions.querySelector('[data-chat-action="copy"]')?.addEventListener('click', (event) => {
      this.copyToClipboard(markdown, event.currentTarget)
    })

    actions.querySelector('[data-chat-action="expand"]')?.addEventListener('click', () => this.openModal())

    wrap.appendChild(actions)
  }

  sendMessage () {
    const content = this.input.value.trim()

    if (!content || this.sendButton.disabled) return

    this.history.push({ role: 'user', content })
    this.appendBubble('user', content)
    this.input.value = ''
    this.sendButton.disabled = true

    const pending = this.appendBubble('assistant', null)

    fetch(this.dataset.chatUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': this.csrfToken },
      body: JSON.stringify({ messages: this.history, attachment_ids: this.attachments.map((a) => a.id) })
    }).then((response) => {
      return response.json().then((data) => ({ ok: response.ok, data }))
    }).then((result) => {
      this.sendButton.disabled = false

      if (result.ok && result.data.reply) {
        this.history.push({ role: 'assistant', content: result.data.reply })
        this.renderAssistant(pending.wrap, pending.bubble, result.data.reply)

        if (result.data.fallback_used && result.data.model_used) {
          this.messages.appendChild(this.buildElement('div', 'text-xs opacity-60 pl-2',
            (this.dataset.tFallback || '').replace('__MODEL__', result.data.model_used)))
        }

        this.scrollToBottom()
      } else {
        this.history.pop()
        pending.bubble.textContent = result.data.error || 'Error'
        pending.bubble.classList.add('chat-bubble-error')
      }
    }).catch(() => {
      this.sendButton.disabled = false
      this.history.pop()
      pending.bubble.textContent = 'Error'
      pending.bubble.classList.add('chat-bubble-error')
    })
  }

  conversationMarkdown () {
    return this.history.map((message) => {
      return `**${message.role === 'user' ? 'You' : 'Assistant'}:**\n\n${message.content}`
    }).join('\n\n---\n\n')
  }

  // ---- Clipboard (copies RAW markdown; the user keeps seeing rich text) ----
  copyToClipboard (text, button) {
    if (!navigator.clipboard) return

    navigator.clipboard.writeText(text).then(() => {
      if (!button) return

      button.classList.add('thor-chip-copied')
      const label = button.querySelector('[data-chat-copy-label]')
      const original = label?.textContent

      if (label) label.textContent = this.dataset.tCopied || 'Copied'

      setTimeout(() => {
        button.classList.remove('thor-chip-copied')

        if (label && original !== undefined) label.textContent = original
      }, 1200)
    })
  }

  // ---- Attachments (extra documents for analysis) ----
  uploadAttachment (file) {
    const maxMb = parseInt(this.dataset.maxFileMb || '15', 10)

    if (file.size > maxMb * 1024 * 1024) {
      this.flashNotice(this.dataset.tFileTooLarge)

      return
    }

    const formData = new FormData()

    formData.append('file', file)

    this.attachButton.disabled = true

    fetch(this.dataset.attachmentsUrl, {
      method: 'POST',
      headers: { 'X-CSRF-Token': this.csrfToken },
      body: formData
    }).then((response) => {
      return response.json().then((data) => ({ ok: response.ok, data }))
    }).then((result) => {
      this.attachButton.disabled = false

      if (result.ok && result.data.id) {
        this.attachments.push({ id: result.data.id, name: result.data.name })
        this.renderChip(result.data.id, result.data.name)
      } else {
        this.flashNotice(result.data.error || this.dataset.tUploadFailed)
      }
    }).catch(() => {
      this.attachButton.disabled = false
      this.flashNotice(this.dataset.tUploadFailed)
    })
  }

  renderChip (id, name) {
    if (!this.chipTemplate) return

    const chip = this.chipTemplate.content.cloneNode(true).firstElementChild

    chip.querySelector('[data-chat-chip-name]').textContent = name

    chip.querySelector('[data-chat-chip-remove]')?.addEventListener('click', () => {
      this.attachments = this.attachments.filter((attachment) => attachment.id !== id)
      chip.remove()
    })

    this.chips.appendChild(chip)
    this.chips.hidden = false
  }

  flashNotice (text) {
    const notice = this.buildElement('div', 'text-xs text-error px-1 pt-1', text || 'Error')

    this.chips.parentNode.insertBefore(notice, this.chips.nextSibling)

    setTimeout(() => notice.remove(), 4000)
  }
}
